import { folioCorto } from "@/lib/formato";
import { IconoAdjunto, IconoSeleccionar } from "@/ui/Iconos";
import { ToggleTrack } from "@/ui/Toggle";
import type { MensajeBuzon, TipoVeredicto } from "@/lib/api";

/**
 * Un renglón del buzón. **No muestra un correo: muestra un veredicto.**
 *
 * Esa es la idea que separa esta lista de un cliente de correo. Antes de leer
 * nada ya sabes si el correo es de un caso tuyo, si parece de un cliente o si
 * la IA lo rescató — y por qué. Una lista de decisiones sugeridas, no de correos.
 *
 * Tres decisiones que vienen de medir, no de gusto:
 *
 * 1. **El adelanto va en `zinc-400`, no `zinc-500`.** `zinc-500` sobre el fondo
 *    de la tarjeta da 3.82:1 y reprueba el 4.5:1 que pide texto; `zinc-400` da
 *    7.20:1. Es el texto que más se lee de toda la vista.
 * 2. **El porqué se LEE, no se adivina.** Antes vivía en el `title` de la
 *    pastilla y «entra solo» se escondía en móvil: en el celular de Joe (412 px,
 *    sin hover) el veredicto no explicaba nada. Cero tooltips (regla de la
 *    casa, 2026-10-13): el motivo va en su propio renglón chiquito, y lo que
 *    significa cada pastilla, en la Ayuda de la ventana.
 * 3. **Las pastillas van en UNA línea, sin `flex-wrap`.** Medido: con el
 *    `flex-wrap` suelto, una fila explota a ~230 px de alto a 390 px de ancho —
 *    50 correos serían 11,500 px dentro de una hoja de 92dvh. Son tres como
 *    mucho (veredicto, «entra solo», adjuntos) y el veredicto se recorta si no
 *    caben; el motivo de abajo lo dice completo.
 *
 * La selección se ve como switch (preferencia de la casa) pero por debajo es un
 * `<input type="checkbox">` real para teclado y lectores de pantalla. Y el
 * control va como HERMANO del área que abre el correo: un control interactivo
 * dentro de otro es HTML inválido.
 */

/** Color de cada pastilla. Exportado para que la Ayuda de «Revisar mi correo»
 *  las pinte igual (`components/ayuda/vistas/revisarCorreo`). */
export const COLOR_VEREDICTO: Record<TipoVeredicto, string> = {
  ancla: "border-accent-500/30 bg-accent-500/10 text-accent-300",
  proveedor: "border-orange-500/30 bg-orange-500/10 text-orange-300",
  cliente: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  clave: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  ia: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  ya_esta: "border-ink-600 bg-ink-800 text-zinc-400",
  // Pendiente de verdad: está en la casa pero sin expediente.
  sin_caso: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  // Decisión tomada, pero visible: descartar no es borrar.
  descartado: "border-ink-600 bg-ink-800 text-zinc-500",
  ignorado: "border-ink-600 bg-ink-800 text-zinc-400",
  ruido: "border-ink-600 bg-ink-800 text-zinc-500",
  indeciso: "border-ink-600 bg-ink-800 text-zinc-400",
};

function etiquetaVeredicto(m: MensajeBuzon): string {
  const v = m.veredicto;
  switch (v.tipo) {
    case "ancla": {
      const corto = folioCorto(v.caso_folio);
      return v.caso_cerrado ? `${corto} (cerrado)` : (corto || "De un caso tuyo");
    }
    case "proveedor":
      return "Parece del proveedor";
    case "cliente":
      return "Parece de un cliente";
    case "clave":
      return "Suena a trabajo";
    case "ia":
      return "✨ Podría servir";
    case "ya_esta":
      return v.caso_folio ? `Ya está en ${folioCorto(v.caso_folio)}` : "Ya está en ArkTickets";
    case "sin_caso":
      return "En tu bandeja, sin caso";
    case "descartado":
      return "Lo descartaste";
    case "ignorado":
      return "Lo ignoraste";
    case "ruido":
      return "Automático";
    default:
      return "Sin clasificar";
  }
}

function fechaCorta(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  return mismoDia
    ? d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

type Props = {
  mensaje: MensajeBuzon;
  seleccionado: boolean;
  activo: boolean;
  seleccionable: boolean;
  onSeleccionar: () => void;
  onAbrir: () => void;
};

export default function FilaCorreoBuzon({
  mensaje: m,
  seleccionado,
  activo,
  seleccionable,
  onSeleccionar,
  onAbrir,
}: Props) {
  const v = m.veredicto;
  const quien = m.de_nombre || m.de;
  // El folio completo, si el motivo no lo trae ya («Ya está en el CASO-…»).
  const folioExtra =
    v.caso_folio && !v.motivo.includes(v.caso_folio) ? ` · ${v.caso_folio}` : "";

  return (
    <li
      className={
        "flex items-start gap-2 px-2 py-2 -mx-2 rounded transition " +
        (activo ? "bg-ink-800" : "hover:bg-ink-800/50")
      }
    >
      {seleccionable ? (
        <label className="flex shrink-0 items-center pt-1 cursor-pointer">
          <input
            type="checkbox"
            className="sr-only"
            checked={seleccionado}
            onChange={onSeleccionar}
            aria-label={`Seleccionar el correo «${m.asunto || "sin asunto"}»`}
          />
          <ToggleTrack checked={seleccionado} />
        </label>
      ) : (
        <span className="w-9 shrink-0" aria-hidden="true" />
      )}

      <button
        type="button"
        onClick={onAbrir}
        className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-accent-500 rounded"
      >
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">{quien}</span>
          <span className="shrink-0 text-[10px] text-zinc-400">{fechaCorta(m.fecha)}</span>
        </div>

        <p className="mt-0.5 truncate text-sm text-zinc-300">{m.asunto || "(sin asunto)"}</p>

        {/* `min-w-0` no es decorativo: sin él, la pastilla del folio —que es
            `shrink-0` y larga— le impone un ancho mínimo a toda la fila y el
            `truncate` del asunto deja de funcionar. Se ve como texto cortado a
            la mitad, sin puntos suspensivos, y en móvil desborda. */}
        <div className="mt-1 flex min-w-0 items-center gap-1.5 overflow-hidden">
          <span
            className={
              "min-w-0 max-w-full truncate rounded border px-1.5 py-0.5 " +
              "text-[10px] uppercase tracking-wide " +
              COLOR_VEREDICTO[v.tipo]
            }
          >
            {etiquetaVeredicto(m)}
          </span>
          {v.engancha_solo && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded border border-accent-500/30
                         bg-accent-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide
                         text-accent-300"
            >
              <IconoSeleccionar className="h-3 w-3" />
              entra solo
            </span>
          )}
          {m.adjuntos > 0 && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-500/30
                         bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-300"
            >
              <IconoAdjunto className="h-3 w-3" />
              {m.adjuntos}
              <span className="sr-only">{m.adjuntos === 1 ? " archivo adjunto" : " archivos adjuntos"}</span>
            </span>
          )}
        </div>

        {/* El porqué del veredicto, a la vista: antes solo salía al pasar el
            mouse por la pastilla. Se parte en renglones si no cabe. */}
        <p className="mt-0.5 text-[11px] leading-snug text-zinc-400">
          <span className="text-zinc-300">Por qué:</span> {v.motivo}
          {folioExtra}
        </p>

        {m.adelanto ? (
          <p className="mt-1 text-xs text-zinc-400 line-clamp-1 sm:line-clamp-2">{m.adelanto}</p>
        ) : (
          <p className="mt-1 text-xs italic text-zinc-500">(sin texto, solo archivos)</p>
        )}
      </button>
    </li>
  );
}
